import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Hash, Type, AlignLeft } from 'lucide-react';

/**
 * ProductServiceForm component - Add/edit product/service modal form
 * Handles both creating new products/services and editing existing ones
 */
const ProductServiceForm = ({ 
  isOpen, 
  onClose, 
  product = null, // null for add, product object for edit
  onSave,
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: '',
    item_code: '',
    item_type: 'Service',
    category: '',
    category_level1: '',
    category_level2: '',
    category_level3: '',
    sales_description: '',
    purchase_description: '',
    quantity_on_hand: '',
    reorder_point: '',
    income_account: 'Services',
    expense_account: '',
    stock_asset_account: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!product;

  // Initialize form data when product prop changes
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        item_code: product.item_code || '',
        item_type: product.item_type || 'Service',
        category: product.category || '',
        category_level1: product.category_level1 || '',
        category_level2: product.category_level2 || '',
        category_level3: product.category_level3 || '',
        sales_description: product.sales_description || '',
        purchase_description: product.purchase_description || '',
        quantity_on_hand: product.quantity_on_hand || '',
        reorder_point: product.reorder_point || '',
        income_account: product.income_account || 'Services',
        expense_account: product.expense_account || '',
        stock_asset_account: product.stock_asset_account || '',
        is_active: product.is_active !== undefined ? !!product.is_active : true
      });
    } else {
      setFormData({
        name: '',
        item_code: '',
        item_type: 'Service',
        category: '',
        category_level1: '',
        category_level2: '',
        category_level3: '',
        sales_description: '',
        purchase_description: '',
        quantity_on_hand: '',
        reorder_point: '',
        income_account: 'Services',
        expense_account: '',
        stock_asset_account: '',
        is_active: true
      });
    }
    setErrors({});
  }, [product, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be less than 255 characters';
    }

    if (formData.item_code && formData.item_code.length > 100) {
      newErrors.item_code = 'Item code must be less than 100 characters';
    }


    if (formData.quantity_on_hand && (isNaN(formData.quantity_on_hand) || parseInt(formData.quantity_on_hand) < 0)) {
      newErrors.quantity_on_hand = 'Quantity must be a valid positive number';
    }

    if (formData.reorder_point && (isNaN(formData.reorder_point) || parseInt(formData.reorder_point) < 0)) {
      newErrors.reorder_point = 'Reorder point must be a valid positive number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert string numbers to appropriate types
      const submitData = {
        ...formData,
        quantity_on_hand: formData.quantity_on_hand ? parseInt(formData.quantity_on_hand) : 0,
        reorder_point: formData.reorder_point ? parseInt(formData.reorder_point) : 0,
      };

      await onSave(submitData);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting && !loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 bg-gradient-to-r from-pest-blue to-pest-teal">
          <h2 className="text-xl md:text-2xl font-bold text-white">
            {isEditing ? 'Edit Product/Service' : 'Add Product/Service'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting || loading}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <div className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                  <Type className="w-5 h-5" />
                  Basic Information
                </h3>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                    errors.name ? 'border-red-500 bg-red-50' : ''
                  }`}
                  placeholder="Enter product/service name"
                  disabled={isSubmitting || loading}
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle size={16} />
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Item Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item Code
                </label>
                <input
                  type="text"
                  value={formData.item_code}
                  onChange={(e) => handleInputChange('item_code', e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                    errors.item_code ? 'border-red-500 bg-red-50' : ''
                  }`}
                  placeholder="Enter item code (optional)"
                  disabled={isSubmitting || loading}
                />
                {errors.item_code && (
                  <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle size={16} />
                    {errors.item_code}
                  </p>
                )}
              </div>

              {/* Item Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type
                </label>
                <select
                  value={formData.item_type}
                  onChange={(e) => handleInputChange('item_type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  disabled={isSubmitting || loading}
                >
                  <option value="Service">Service</option>
                  <option value="Product">Product</option>
                </select>
              </div>

              {/* Category Level 1 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category Level 1
                </label>
                <input
                  type="text"
                  value={formData.category_level1}
                  onChange={(e) => handleInputChange('category_level1', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  placeholder="Enter category level 1"
                  disabled={isSubmitting || loading}
                />
              </div>
            </div>


            {/* Descriptions */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                <AlignLeft className="w-5 h-5" />
                Descriptions
              </h3>

              {/* Sales Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sales Description
                </label>
                <textarea
                  value={formData.sales_description}
                  onChange={(e) => handleInputChange('sales_description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  rows={3}
                  placeholder="Description shown to customers"
                  disabled={isSubmitting || loading}
                />
              </div>

              {/* Purchase Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Purchase Description
                </label>
                <textarea
                  value={formData.purchase_description}
                  onChange={(e) => handleInputChange('purchase_description', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors"
                  rows={3}
                  placeholder="Description for internal use"
                  disabled={isSubmitting || loading}
                />
              </div>
            </div>

            {/* Inventory (Only for Products) */}
            {formData.item_type === 'Product' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Hash className="w-5 h-5" />
                    Inventory
                  </h3>
                </div>

                {/* Quantity on Hand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Quantity on Hand
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity_on_hand}
                    onChange={(e) => handleInputChange('quantity_on_hand', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                      errors.quantity_on_hand ? 'border-red-500 bg-red-50' : ''
                    }`}
                    placeholder="0"
                    disabled={isSubmitting || loading}
                  />
                  {errors.quantity_on_hand && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle size={16} />
                      {errors.quantity_on_hand}
                    </p>
                  )}
                </div>

                {/* Reorder Point */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reorder Point
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.reorder_point}
                    onChange={(e) => handleInputChange('reorder_point', e.target.value)}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pest-blue focus:border-pest-blue transition-colors ${
                      errors.reorder_point ? 'border-red-500 bg-red-50' : ''
                    }`}
                    placeholder="0"
                    disabled={isSubmitting || loading}
                  />
                  {errors.reorder_point && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                      <AlertCircle size={16} />
                      {errors.reorder_point}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Status toggle removed - now on card row */}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting || loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || loading}
            className="px-6 py-2 bg-pest-blue hover:bg-pest-blue-dark text-white rounded-lg font-medium transition-colors duration-200 flex items-center gap-2 disabled:opacity-50"
          >
            {(isSubmitting || loading) ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save size={18} />
            )}
            {isSubmitting || loading ? 'Saving...' : 'Save Product/Service'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductServiceForm;